"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import type { MASS_ENERGY_EQUIVALENCE_CALCULATOR_STRINGS } from "@/components/tools/mass-energy-equivalence-calculator";
import { EnergyScale } from "@/components/tools/physics-fields/mass-energy-diagram";
import {
  carrySolved,
  DiagramFrame,
  type SolveFieldSpec,
  SolveGrid,
  unitOptions,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import { useToolNumber } from "@/components/tools/tool-ui";
import { SPEED_OF_LIGHT } from "@/lib/tool-kit/physics-fields/constants";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  type MassEnergyError,
  type MovingKnown,
  pickUnit,
  type RestTarget,
  solveMoving,
  solveRest,
} from "@/lib/tool-kit/physics-fields/mass-energy";
import {
  movingWorking,
  restWorking,
} from "@/lib/tool-kit/physics-fields/mass-energy-working";
import {
  ENERGY_UNITS,
  findUnit,
  fromBase,
  RELATIVISTIC_SPEED_UNITS,
  REST_MASS_UNITS,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof MASS_ENERGY_EQUIVALENCE_CALCULATOR_STRINGS;

interface PanelProps {
  strings: Strings;
  sig: number;
}

const TNT_IDS = ["tTNT", "ktTNT", "MtTNT"] as const;
const EV_IDS = ["eV", "keV", "MeV", "GeV", "TeV"] as const;
const TEV_FACTOR = findUnit(ENERGY_UNITS, "TeV").factor;
/** Below one kilogram of TNT the explosive equivalent is not worth stating. */
const TNT_THRESHOLD_TONNES = 1e-3;

function Failure({
  error,
  strings,
}: {
  error: MassEnergyError;
  strings: Strings;
}) {
  const messages: Record<MassEnergyError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    negative: strings.errorNegative,
    "light-speed": strings.errorLightSpeed,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

/** A value in a named energy unit, with the unit's symbol. */
function energyIn(
  unitId: string,
  joules: number,
  fmt: Parameters<typeof formatSig>[0],
  sig: number
): string {
  return `${formatSig(fmt, fromBase(ENERGY_UNITS, unitId, joules), sig)} ${findUnit(ENERGY_UNITS, unitId).symbol}`;
}

/** Electronvolts with a sensible prefix, or plain eV when even TeV would be unwieldy. */
function electronVolts(
  joules: number,
  fmt: Parameters<typeof formatSig>[0],
  sig: number
): string {
  const id =
    joules / TEV_FACTOR > 1000 ? "eV" : pickUnit(ENERGY_UNITS, EV_IDS, joules);
  return energyIn(id, joules, fmt, sig);
}

/* ── Mass ↔ energy ────────────────────────────────────────────────────────── */

type RestKey = "m" | "e";

const REST_EXAMPLES: Record<
  string,
  {
    text: Record<RestKey, string>;
    unit: Record<RestKey, string>;
    target: RestTarget;
  }
> = {
  gram: {
    text: { m: "1", e: "" },
    unit: { m: "g", e: "GWh" },
    target: "energy",
  },
  kilogram: {
    text: { m: "1", e: "" },
    unit: { m: "kg", e: "PJ" },
    target: "energy",
  },
  electron: {
    text: { m: "9.1093837139e-31", e: "" },
    unit: { m: "kg", e: "MeV" },
    target: "energy",
  },
  proton: {
    text: { m: "1.67262192595e-27", e: "" },
    unit: { m: "kg", e: "MeV" },
    target: "energy",
  },
  amu: {
    text: { m: "1", e: "" },
    unit: { m: "u", e: "MeV" },
    target: "energy",
  },
  bomb: {
    text: { m: "", e: "15" },
    unit: { m: "g", e: "ktTNT" },
    target: "mass",
  },
  sun: {
    text: { m: "", e: "3.828e26" },
    unit: { m: "t", e: "J" },
    target: "mass",
  },
  kwh: { text: { m: "", e: "1" }, unit: { m: "ug", e: "kWh" }, target: "mass" },
};

export function RestPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("gram");
  const [target, setTarget] = useState<RestTarget>("energy");
  const [text, setText] = useState<Record<RestKey, string>>(
    REST_EXAMPLES.gram.text
  );
  const [unit, setUnit] = useState<Record<RestKey, string>>(
    REST_EXAMPLES.gram.unit
  );

  const outcome = solveRest(
    readQuantity(text.m, REST_MASS_UNITS, unit.m),
    readQuantity(text.e, ENERGY_UNITS, unit.e),
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<RestKey>[] = [
    {
      key: "m",
      label: strings.mass,
      units: REST_MASS_UNITS,
      solved: target === "mass",
      hint: strings.massHint,
    },
    {
      key: "e",
      label: strings.energy,
      units: ENERGY_UNITS,
      solved: target === "energy",
    },
  ];
  const solvedSI = { m: value?.mass ?? null, e: value?.energy ?? null };
  const names = strings.restExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = REST_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
      setTarget(ex.target);
    }
  };
  const tntUnit =
    value === null ? "tTNT" : pickUnit(ENERGY_UNITS, TNT_IDS, value.energy);

  return (
    <>
      <SelectField
        id="me-rest-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(REST_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.solveFor}
        name="me-rest-solve"
        onChange={(next: RestTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "energy", label: strings.energyShort },
          { value: "mass", label: strings.massShort },
        ]}
        value={target}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="me-rest"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.energyJoules}
                value={`${formatSig(fmt, value.energy, sig)} J`}
              />
              <StatCard
                label={strings.energyKwh}
                value={energyIn("kWh", value.energy, fmt, sig)}
              />
              <StatCard
                label={strings.energyTnt}
                value={
                  value.tntTonnes < TNT_THRESHOLD_TONNES
                    ? "—"
                    : energyIn(tntUnit, value.energy, fmt, sig)
                }
              />
              <StatCard
                label={strings.energyEv}
                value={electronVolts(value.energy, fmt, sig)}
              />
              <StatCard
                label={strings.massKg}
                value={`${formatSig(fmt, value.mass, sig)} kg`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={restWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.scaleCaption}>
              <EnergyScale
                benchmarks={[
                  { joules: 3.6e6, label: strings.benchKwh },
                  { joules: 4.184e9, label: strings.benchTonne },
                  { joules: 15 * 4.184e12, label: strings.benchKilotons },
                  { joules: 50 * 4.184e15, label: strings.benchMegatons },
                  { joules: 3.828e26, label: strings.benchSun },
                ]}
                description={strings.scaleDescription}
                id="me-scale"
                joules={value.energy}
                label={strings.scaleLabel}
                markerText={strings.thisEnergy}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

/* ── A moving particle ────────────────────────────────────────────────────── */

type MoveKey = "m" | "x";

const MOVING_EXAMPLES: Record<
  string,
  {
    known: MovingKnown;
    text: Record<MoveKey, string>;
    unit: Record<MoveKey, string>;
    out: string;
  }
> = {
  electron: {
    known: "kinetic",
    text: { m: "9.1093837139e-31", x: "1" },
    unit: { m: "kg", x: "MeV" },
    out: "MeV",
  },
  proton: {
    known: "speed",
    text: { m: "1.67262192595e-27", x: "0.5" },
    unit: { m: "kg", x: "c" },
    out: "MeV",
  },
  lhc: {
    known: "kinetic",
    text: { m: "1.67262192595e-27", x: "6.8" },
    unit: { m: "kg", x: "TeV" },
    out: "TeV",
  },
  car: {
    known: "speed",
    text: { m: "1000", x: "100" },
    unit: { m: "kg", x: "km/h" },
    out: "kJ",
  },
};

export function MovingPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const start = MOVING_EXAMPLES.electron;
  const [example, setExample] = useState("electron");
  const [known, setKnown] = useState<MovingKnown>(start.known);
  const [text, setText] = useState<Record<MoveKey, string>>(start.text);
  const [unit, setUnit] = useState<Record<MoveKey, string>>(start.unit);
  const [out, setOut] = useState(start.out);

  const valueUnits =
    known === "speed" ? RELATIVISTIC_SPEED_UNITS : ENERGY_UNITS;
  const outcome = solveMoving(
    readQuantity(text.m, REST_MASS_UNITS, unit.m),
    known,
    readQuantity(text.x, valueUnits, unit.x)
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<MoveKey>[] = [
    {
      key: "m",
      label: strings.mass,
      units: REST_MASS_UNITS,
      solved: false,
      hint: strings.massHint,
    },
    {
      key: "x",
      label: known === "speed" ? strings.speed : strings.kinetic,
      units: valueUnits,
      solved: false,
    },
  ];
  const names = strings.movingExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = MOVING_EXAMPLES[id];
    if (ex) {
      setKnown(ex.known);
      setText(ex.text);
      setUnit(ex.unit);
      setOut(ex.out);
    }
  };
  const changeKnown = (next: MovingKnown) => {
    setKnown(next);
    setExample("custom");
    setText({ ...text, x: next === "speed" ? "0.5" : "1" });
    setUnit({ ...unit, x: next === "speed" ? "c" : "MeV" });
  };
  const energy = (joules: number) => energyIn(out, joules, fmt, sig);

  return (
    <>
      <SelectField
        id="me-moving-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(MOVING_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.knownLabel}
        name="me-moving-known"
        onChange={changeKnown}
        options={[
          { value: "speed", label: strings.knownSpeed },
          { value: "kinetic", label: strings.knownKinetic },
        ]}
        value={known}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="me-moving"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={{}}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <SelectField
        id="me-moving-out"
        label={strings.showEnergiesIn}
        onChange={setOut}
        options={unitOptions(ENERGY_UNITS)}
        value={out}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.totalEnergy}
                value={energy(value.totalEnergy)}
              />
              <StatCard
                label={strings.restEnergy}
                value={energy(value.restEnergy)}
              />
              <StatCard
                label={strings.kineticEnergy}
                value={energy(value.kinetic)}
              />
              <StatCard
                label={strings.classicalKinetic}
                value={energy(value.classicalKinetic)}
              />
              <StatCard
                label={strings.ratio}
                value={formatSig(fmt, value.ratio, sig)}
              />
              <StatCard
                label={strings.gamma}
                value={formatSig(fmt, value.gamma, Math.max(sig, 5))}
              />
              <StatCard
                label={strings.beta}
                value={formatSig(fmt, value.beta, Math.max(sig, 6))}
              />
              <StatCard
                label={strings.speedResult}
                value={`${formatSig(fmt, value.speed, Math.max(sig, 5))} m/s`}
              />
              <StatCard
                label={strings.shortfall}
                value={`${formatSig(fmt, value.shortfall, sig)} m/s`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={movingWorking(known, value, fmt, sig)}
            />
            <p className="text-muted-foreground text-xs">
              {`c = ${fmt(SPEED_OF_LIGHT)} m/s`}
            </p>
          </>
        )}
      </section>
    </>
  );
}
